/**
 * Generates a URL-friendly slug from a string
 * @param text - The text to slugify
 * @returns A lowercase, hyphenated string safe for URLs
 */
export function slugify(text: string): string {
  return text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumeric chars except spaces and hyphens
    .replace(/[\s_]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Generates a full slug with ID for dive center URLs
 * Format: dive-center-name-id
 * @param name - The dive center name
 * @param id - The dive center ID
 * @returns Full slug with ID appended
 */
export function generateDiveCenterSlug(name: string | null | undefined, id: string): string {
  if (!name) {
    return id;
  }
  const nameSlug = slugify(name);
  return nameSlug ? `${nameSlug}-${id}` : id;
}

/**
 * Extracts the dive center ID from a slug
 * The ID is always the last segment after the final hyphen (CUID format)
 * @param slug - The full slug (e.g., "blue-corner-dive-cmahsgd8e000itz0d6zvtqpsd")
 * @returns The extracted ID
 */
export function extractIdFromSlug(slug: string): string {
  // CUID format: starts with 'c' and is 25 characters long
  // The ID is at the end of the slug
  const cuidPattern = /c[a-z0-9]{24}$/;
  const match = slug.match(cuidPattern);
  
  if (match) {
    return match[0];
  }

  // Some ids were created by hand and are not 25-char CUIDs
  // (e.g. "cm9dpk0001dcen0000000001"). IDs never contain hyphens,
  // so take the last segment when it looks like an id.
  const lastSegment = slug.slice(slug.lastIndexOf('-') + 1);
  if (/^c[a-z0-9]{19,29}$/.test(lastSegment)) {
    return lastSegment;
  }

  // Fallback: if no CUID pattern found, return the whole slug (might be just an ID)
  return slug;
}

/**
 * Generates the URL path for a dive center
 * @param name - The dive center name
 * @param id - The dive center ID
 * @returns The URL path (e.g., "/dive-center/blue-corner-dive-cmahsgd8e000itz0d6zvtqpsd")
 */
export function getDiveCenterUrl(name: string | null | undefined, id: string): string {
  const slug = generateDiveCenterSlug(name, id);
  return `/dive-center/${slug}`;
}
